import React, { useState } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, DollarSign, Calendar, MessageCircle } from 'lucide-react';
import carnotfound from './carnotfound.png';

const OfferContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 20px;
  margin: 16px 0;
  position: relative;
`;

const OfferHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const OfferTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const OfferBadge = styled.span`
  background: linear-gradient(45deg, #F43F60,rgb(235, 92, 118));
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
`;

const VehicleInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
    @media (max-width: 575px) {
       display: block;
  }
`;

const VehicleImage = styled.img`
  width: 100px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  @media (max-width: 575px) {
    margin-bottom: 20px;
  }
`;

const VehicleDetails = styled.div`
  flex: 1;
`;

const VehicleTitle = styled.h4`
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
`;

const VehicleSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin: 0;
`;

const VehicleSpecs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const SpecBadge = styled.span`
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OriginalPrice = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 16px;
  text-decoration: line-through;
`;

const OfferPrice = styled.span`
  color: #4ECDC4;
  font-size: 24px;
  font-weight: 700;
`;

const Savings = styled.span`
  color: #F43F60;
  font-size: 16px;
  font-weight: 500;
`;

const ActionButtons = styled.div`
    display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 16px;
`;

const AcceptButton = styled.button`
  background: linear-gradient(45deg, #4ECDC4, #44A08D);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(78, 205, 196, 0.3);
  }
`;

const DeclineButton = styled.button`
  background: rgba(244, 63, 96, 0.1);
  color: #F43F60;
  border: 1px solid rgba(244, 63, 96, 0.3);
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;

  &:hover {
    background: rgba(244, 63, 96, 0.2);
    border-color: rgba(244, 63, 96, 0.5);
  }
`;

const QuickResponseButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const QuickResponseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const OfferMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
    @media (max-width: 767px) {
     margin-bottom: 20px;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
`;

const OfferDetails = ({ offerData, onAccept, onDecline, onQuickResponse }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const askingPrice = offerData.vehicle.price;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const calculateSavings = () => {
    if (askingPrice && offerData.offer.amount) {
      return askingPrice - offerData.offer.amount;
    }
    return 0;
  };

  const savings = calculateSavings();

  const quickResponses = [
    "Too expensive",
    "Not in my budget",
    "Need more time to think",
    "Looking for different features",
    "Can you do better?",
    "Interested but need financing",
  ];

  const handleAccept = () => {
    onAccept && onAccept(offerData);
  };

  const handleDecline = () => {
    onDecline && onDecline(offerData);
  };

  const handleQuickResponse = (response) => {
    onQuickResponse && onQuickResponse(offerData, response);
  };

  return (
    <OfferContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <OfferHeader>
        <OfferTitle>
          <DollarSign size={20} />
          Special Offer
          <OfferBadge>Limited Time</OfferBadge>
        </OfferTitle>
      </OfferHeader>

      <VehicleInfo>
      <VehicleImage 
          src={(() => {
            // Parse images from imagelist string
            let images = [];
            try {
              if (offerData.vehicle?.imagelist && typeof offerData.vehicle.imagelist === 'string') {
                try {
                  // Try parsing as JSON first
                  images = JSON.parse(offerData.vehicle.imagelist);
                } catch {
                  // If JSON parse fails, treat as CSV
                  images = offerData.vehicle.imagelist.split(',').map(url => ({ url: url.trim() }));
                }
              }
            } catch (e) {
              console.error('Error parsing imagelist:', e);
            }
            return images?.[0]?.url || carnotfound;
          })()}
          alt={`${offerData.vehicle.year} ${offerData.vehicle.make} ${offerData.vehicle.model}`}
          onError={(e) => {
            e.target.src = carnotfound;
          }}
        />
        <VehicleDetails>
          <VehicleTitle>
            {offerData.vehicle.year} {offerData.vehicle.make} {offerData.vehicle.model} {offerData.vehicle.trim}
          </VehicleTitle>
          <VehicleSubtitle>
            Stock: {offerData.vehicle.stock_no} • VIN: {offerData.vehicle.vin?.slice(-8)}
          </VehicleSubtitle>
          <VehicleSpecs>
            <SpecBadge>{offerData.vehicle.mileage} mi</SpecBadge>
            <SpecBadge>{offerData.vehicle.engine}</SpecBadge>
            <SpecBadge>{offerData.vehicle.transmission}</SpecBadge>
            <SpecBadge>{offerData.vehicle.drivetrain}</SpecBadge>
            {offerData.vehicle.ext_color && <SpecBadge>{offerData.vehicle.ext_color}</SpecBadge>}
            {offerData.vehicle.int_color && <SpecBadge>{offerData.vehicle.int_color}</SpecBadge>}
          </VehicleSpecs>
        </VehicleDetails>
      </VehicleInfo>

      <PriceInfo>
        <PriceContainer>
          <OriginalPrice>Original Price: {formatPrice(askingPrice)}</OriginalPrice>
          <OfferPrice>Offer: {formatPrice(offerData?.offer?.amount)}</OfferPrice>
          {savings > 0 && (
            <Savings>Save {formatPrice(savings)}</Savings>
          )}
        </PriceContainer>
      </PriceInfo>
{
    offerData.offer.status === "pending" && (
<>
<ActionButtons>
        <AcceptButton onClick={handleAccept}>
          <Check size={16} />
          Accept Offer
        </AcceptButton>
        <DeclineButton onClick={handleDecline}>
          <X size={16} />
          Decline
        </DeclineButton>
      </ActionButtons>

      <QuickResponseButtons>
        {quickResponses.map((response, index) => (
          <QuickResponseButton
            key={index}
            onClick={() => handleQuickResponse(response)}
          >
            {response}
          </QuickResponseButton>
        ))}
      </QuickResponseButtons>

</>

    )
}
    
      <OfferMeta>
        <MetaInfo>
          <Calendar size={12} />
          Offered on {formatDate(offerData.timestamp)}
        </MetaInfo>
        <MetaInfo>
          <MessageCircle size={12} />
          Status: {offerData?.offer?.status}
        </MetaInfo>
        <MetaInfo>
          <DollarSign size={12} />
          Offer ID: {offerData?.offer?.id}
        </MetaInfo>
      </OfferMeta>
    </OfferContainer>
  );
};

export default OfferDetails; 